import { useState, type FormEvent } from "react";
import { useAppStore } from "@state/store";
import { useBrand } from "@branding/useBrand";

/**
 * Sign-in gate (Identity mandate #1). Shown before the app is usable whenever
 * setup is complete but there is no valid local session. Authenticates against
 * a local user via the store; on success the session is persisted and the app
 * shell takes over. Branded to match the Setup Wizard.
 */
export function SignIn() {
  const brand = useBrand();
  const signIn = useAppStore((s) => s.signIn);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const initial = (brand.name.trim()[0] || "L").toUpperCase();
  const canSubmit = email.trim().length > 0 && password.length > 0 && !submitting;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const ok = await signIn(email, password);
    if (!ok) {
      setError("Incorrect email or password.");
      setSubmitting(false);
    }
    // On success the app re-renders past the gate; no further work needed.
  }

  return (
    <div className="setup">
      <main className="setup-panel" style={{ maxWidth: 440 }}>
        <div className="brand" aria-label={`${brand.name} sign in`}>
          <div className="brand-mark" aria-hidden="true">
            {initial}
          </div>
          <div>
            <div className="brand-name">{brand.name}</div>
            <div className="brand-tag">{brand.tagline}</div>
          </div>
        </div>

        <div className="page-head mt5">
          <div>
            <h1>Sign in</h1>
            <div className="sub">
              Enter your credentials to access {brand.name}. Your ledger is stored
              locally in this browser.
            </div>
          </div>
        </div>

        <form className="card mt4" onSubmit={onSubmit}>
          <div className="card-body">
            <div className="field">
              <label htmlFor="signInEmail">Email</label>
              <input
                id="signInEmail"
                className="input"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoFocus
              />
            </div>
            <div className="field mt4">
              <label htmlFor="signInPassword">Password</label>
              <input
                id="signInPassword"
                className="input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {error ? (
              <div className="badge bad mt4" role="alert">
                <span className="dot" /> {error}
              </div>
            ) : null}

            <div className="mt5">
              <button type="submit" className="btn primary" disabled={!canSubmit}>
                {submitting ? "Signing in…" : "Sign in"}
              </button>
            </div>
          </div>
        </form>

        <p className="hint mt4">{brand.legalFooter}</p>

        <p className="hint mt4" style={{ textAlign: "center" }}>
          Ledgerline — secure multi-entity ledger
        </p>

        <p className="hint mt3" style={{ textAlign: "center" }}>
          Trusted by UAE finance teams
        </p>
      </main>
    </div>
  );
}
